import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient, QueryKey } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { TransactionAttachment } from '@/types/transaction';
import { useTransactionAttachments } from './useTransactionAttachments';
import { useFileOperations, FileWithDescription } from './storage/useFileOperations';
import { useRealtimeBadgeSync } from '../utils/badgeEvents';

interface AttachmentsState {
  [transactionId: string]: {
    attachments: TransactionAttachment[];
    isLoading: boolean;
    lastUpdated: number;
  };
}

interface UseAttachmentsSyncOptions {
  enabled?: boolean;
  staleTime?: number;
  refetchOnWindowFocus?: boolean;
}

export function useAttachmentsSync(options: UseAttachmentsSyncOptions = {}) {
  const {
    enabled = true,
    staleTime = 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus = false,
  } = options;

  const { uploadAttachments } = useTransactionAttachments();
  const {
    getTransactionAttachments: getAttachments,
    deleteAttachment,
    downloadAttachment
  } = useFileOperations();
  const queryClient = useQueryClient();

  // Cache local para armazenar estado dos anexos
  const [localCache, setLocalCache] = useState<AttachmentsState>({});

  // Função auxiliar para atualizar badges de anexos em tempo real
  const updateAttachmentBadges = useCallback(async (transactionId: string, hasAttachments: boolean) => {
    try {
      // Notificar sistema de badges sobre a atualização de anexos
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('transaction-badge-update', {
          detail: {
            transactionId,
            updates: {
              hasAttachments
            }
          }
        }));
      }, 50);

      // Invalidar cache de badges específicos
      queryClient.invalidateQueries({ queryKey: ['transaction-badges', transactionId] });
    } catch (error) {
      console.warn('Failed to update attachment badges:', error);
    }
  }, [queryClient]);

  // Chave de query para React Query
  const getQueryKey = (transactionId: string): QueryKey => ['transaction-attachments', transactionId];

  // Query para buscar anexos de uma transação
  const useTransactionAttachmentsQuery = (transactionId: string) => {
    return useQuery({
      queryKey: getQueryKey(transactionId),
      queryFn: async () => {
        if (!transactionId || !enabled) return [];

        try {
          const attachments = await getAttachments(transactionId);

          // Atualizar cache local
          setLocalCache(prev => ({
            ...prev,
            [transactionId]: {
              attachments,
              isLoading: false,
              lastUpdated: Date.now(),
            },
          }));

          return attachments;
        } catch (error) {
          console.error(`Erro ao buscar anexos da transação ${transactionId}:`, error);
          toast.error('Erro ao carregar anexos');
          throw error;
        }
      },
      enabled: enabled && !!transactionId,
      staleTime,
      refetchOnWindowFocus,
      retry: 2, // Adicionar tentativas de retry
      retryDelay: 1000, // 1 segundo entre tentativas
    });
  };

  // Função para invalidar cache de uma transação específica
  const invalidateTransactionAttachments = useCallback((transactionId: string) => {
    queryClient.invalidateQueries({ queryKey: getQueryKey(transactionId) });

    // Remover do cache local para forçar recarregamento
    setLocalCache(prev => {
      const newCache = { ...prev };
      delete newCache[transactionId];
      return newCache;
    });
  }, [queryClient]);

  // Função para invalidar cache de todas as transações
  const invalidateAllAttachments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['transaction-attachments'] });
    setLocalCache({});
  }, [queryClient]);

  // Upload de anexos com sincronização automática
  const uploadAttachmentsSync = useCallback(async (
    userId: string,
    transactionId: string,
    filesWithDescriptions: FileWithDescription[]
  ): Promise<TransactionAttachment[]> => {
    try {
      // Fazer upload
      const uploadedAttachments = await uploadAttachments(userId, transactionId, filesWithDescriptions);

      // Invalidar cache para forçar recarregamento
      invalidateTransactionAttachments(transactionId);

      // Atualizar badges em tempo real (agora tem anexos)
      await updateAttachmentBadges(transactionId, true);

      // Atualizar timestamp da transação para notificar outros componentes
      await supabase
        .from('transactions')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', transactionId);

      return uploadedAttachments;
    } catch (error) {
      console.error('Erro no upload sincronizado:', error);
      toast.error('Erro ao fazer upload dos anexos');
      throw error;
    }
  }, [uploadAttachments, invalidateTransactionAttachments, updateAttachmentBadges]);

  // Excluir anexo com sincronização automática
  const deleteAttachmentSync = useCallback(async (attachment: TransactionAttachment) => {
    try {
      await deleteAttachment(attachment);

      // Invalidar cache da transação
      invalidateTransactionAttachments(attachment.transaction_id);

      // Verificar se ainda há anexos após a exclusão
      const remainingAttachments = await getAttachments(attachment.transaction_id);
      const hasRemainingAttachments = remainingAttachments && remainingAttachments.length > 0;

      // Atualizar badges em tempo real (pode não ter mais anexos)
      await updateAttachmentBadges(attachment.transaction_id, hasRemainingAttachments);

      // Atualizar timestamp da transação
      await supabase
        .from('transactions')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', attachment.transaction_id);

      toast.success('Anexo excluído com sucesso');
    } catch (error) {
      console.error('Erro na exclusão sincronizada:', error);
      toast.error('Erro ao excluir anexo');
      throw error;
    }
  }, [deleteAttachment, invalidateTransactionAttachments, updateAttachmentBadges, getAttachments]);

  // Atualizar descrição de anexo existente
  const updateAttachmentDescriptionSync = useCallback(async (
    attachmentId: number,
    transactionId: string,
    description: string | null
  ) => {
    try {
      const { error } = await supabase
        .from('transaction_attachments')
        .update({ description })
        .eq('id', attachmentId);

      if (error) throw error;

      // Invalidar cache da transação
      invalidateTransactionAttachments(transactionId);

      // Atualizar timestamp da transação
      await supabase
        .from('transactions')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', transactionId);

      toast.success('Descrição atualizada com sucesso');
    } catch (error) {
      console.error('Erro na atualização sincronizada:', error);
      toast.error('Erro ao atualizar descrição');
      throw error;
    }
  }, [invalidateTransactionAttachments]);

  // Verificar se transação tem anexos (cache local + fallback)
  const hasAttachments = useCallback((transactionId: string): boolean => {
    // Primeiro verificar cache local para performance
    const cached = localCache[transactionId];
    if (cached && cached.attachments.length > 0) {
      return true;
    }

    // Fallback: verificar se há dados no React Query
    const queryData = queryClient.getQueryData(['transaction-attachments', transactionId]);
    if (Array.isArray(queryData) && queryData.length > 0) {
      return true;
    }

    return false;
  }, [localCache, queryClient]);

  // Obter contagem de anexos (cache local + fallback)
  const getAttachmentsCount = useCallback((transactionId: string): number => {
    // Primeiro verificar cache local para performance
    const cached = localCache[transactionId];
    if (cached) {
      return cached.attachments.length;
    }

    // Fallback: verificar dados no React Query
    const queryData = queryClient.getQueryData(['transaction-attachments', transactionId]);
    if (Array.isArray(queryData)) {
      return queryData.length;
    }

    return 0;
  }, [localCache, queryClient]);

  // Pré-carregar anexos para múltiplas transações
  const preloadAttachments = useCallback(async (transactionIds: string[]) => {
    const promises = transactionIds.map(id =>
      queryClient.prefetchQuery({
        queryKey: getQueryKey(id),
        queryFn: () => getAttachments(id),
      })
    );

    await Promise.allSettled(promises);
  }, [queryClient, getAttachments]);

  // Limpar cache de transações não utilizadas
  const cleanupCache = useCallback(() => {
    const now = Date.now();
    const maxAge = 1000 * 60 * 30; // 30 minutos

    setLocalCache(prev => {
      const cleaned = { ...prev };
      Object.keys(prev).forEach(transactionId => {
        if (now - prev[transactionId].lastUpdated > maxAge) {
          delete cleaned[transactionId];
        }
      });
      return cleaned;
    });
  }, []);

  // Limpar cache periodicamente
  useEffect(() => {
    const interval = setInterval(cleanupCache, 1000 * 60 * 10); // A cada 10 minutos
    return () => clearInterval(interval);
  }, [cleanupCache]);

  return {
    // Query hook
    useTransactionAttachmentsQuery,

    // Ações sincronizadas
    uploadAttachmentsSync,
    deleteAttachmentSync,
    updateAttachmentDescriptionSync,
    downloadAttachment,

    // Cache helpers
    invalidateTransactionAttachments,
    invalidateAllAttachments,
    hasAttachments,
    getAttachmentsCount,
    preloadAttachments,
    cleanupCache,

    // Badge synchronization
    updateAttachmentBadges,

    // Estado local
    localCache,
  };
}
